import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { RefreshCandidatesResponse } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { candidateLabel, groupCandidatesByMakeAndLine } from './refreshCandidateGrouping';

const GRID_COLS = '24px minmax(0,3fr) repeat(3, minmax(0,1fr))';

/**
 * Refresh-step candidate-SKU table (SIL-252 T16, spec §3.5/§9 Wave 4):
 * class-filtered, shows fp16 TFLOPS / watts / consolidation ratio, radio-picks
 * the cohort's `refreshSkuId`. Only rendered for `action: 'refresh'` cohorts
 * (caller decides when to mount this).
 *
 * Search + manufacturer filter are client-side over the already-loaded class
 * candidate list (one class ~= low hundreds of SKUs) — no extra fetch.
 *
 * Candidates are grouped into collapsible panels — one per (manufacturer,
 * model line) pair, headed "DELL TECHNOLOGIES | POWEREDGE" — because a flat
 * class list runs to hundreds of rows. Panels are collapsed by default with
 * two exceptions, both about never hiding something the user needs: the panel
 * holding the saved selection, and (while searching) every panel with a hit.
 */
export function RefreshCandidateTable({
  assetClassId,
  assetClassName,
  selectedSkuId,
  onSelect,
}: {
  assetClassId: string;
  assetClassName: string;
  selectedSkuId: string | null;
  onSelect: (skuId: string, consolidationRatio: number | null) => void;
}) {
  const [search, setSearch] = useState('');
  const [make, setMake] = useState('');
  // Explicit user toggles, layered over the auto-expand rules below. Cleared
  // whenever the search changes so a panel the user collapsed earlier cannot
  // swallow a later search hit and read as "no results".
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk.refreshCandidates(assetClassId),
    queryFn: async () => {
      const res = await api.api.client.scenarios['refresh-candidates'].$get({
        query: { assetClassId },
      });
      // Epic 2 (lots-first-class.md) added a 400 (`unsupported_selector_key`)
      // branch to this route, so its RPC-inferred response type is now a
      // union of the success shape and `{error}` — `throwIfNotOk` throws on
      // any non-2xx at runtime but (per lib/api.ts's own docstring) does not
      // narrow the TS union, so `res.json()` alone would type as the union.
      // This route only ever sends assetClassId/assetIds from this caller
      // (never lotId/teamId), so the 400 branch is unreachable from here —
      // the cast documents that rather than threading an error check no
      // caller of this component needs.
      await throwIfNotOk(res);
      return (await res.json()) as RefreshCandidatesResponse;
    },
  });

  const makes = useMemo(
    () =>
      Array.from(
        new Set((data?.candidates ?? []).map((c) => c.make).filter(Boolean) as string[]),
      ).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.candidates ?? []).filter((c) => {
      if (make && c.make !== make) return false;
      if (!q) return true;
      // Match the rendered label too, not just the raw columns — otherwise a
      // user searching what they can see gets nothing.
      return [c.make, c.model, c.displayName, c.skuId, candidateLabel(c)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, make]);

  const groups = useMemo(() => groupCandidatesByMakeAndLine(filtered), [filtered]);

  const searching = search.trim().length > 0;
  const isOpen = (group: (typeof groups)[number]) =>
    toggled[group.key] ??
    // Auto-expand: while searching every surviving panel is a hit, and the
    // panel holding the saved selection must never start hidden on revisit.
    (searching || group.candidates.some((c) => c.skuId === selectedSkuId));

  if (isLoading) {
    return <LoadingShell rows={3} />;
  }
  if (error || !data) {
    return (
      <ErrorState
        title={`Couldn't load candidate SKUs for ${assetClassName}.`}
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }
  if (data.candidates.length === 0) {
    return (
      <div className="rounded-md border border-zinc-200 p-4 text-xs text-zinc-500">
        No catalog SKUs found for {assetClassName}.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setToggled({});
          }}
          placeholder="Search make, model…"
          className="min-w-0 flex-1 rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs text-zinc-800 placeholder:text-zinc-400 focus:border-brand-400 focus:outline-none"
        />
        {makes.length > 1 && (
          <select
            value={make}
            onChange={(e) => setMake(e.target.value)}
            className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs text-zinc-700 focus:border-brand-400 focus:outline-none"
          >
            <option value="">All manufacturers</option>
            {makes.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
        <span className="whitespace-nowrap text-xs text-zinc-400">
          {filtered.length} of {data.candidates.length}
        </span>
      </div>

      <div className="overflow-hidden rounded-md border border-zinc-200">
        <div
          className="grid bg-zinc-50 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-500"
          style={{ gridTemplateColumns: GRID_COLS }}
        >
          <span />
          <span>SKU</span>
          <span className="text-right">fp16 TFLOPS</span>
          <span className="text-right">Watts (TDP)</span>
          <span className="text-right">Consolidation</span>
        </div>
        {filtered.length === 0 ? (
          <div className="border-t border-zinc-100 px-3 py-3 text-xs text-zinc-500">
            No SKUs match your filter.
          </div>
        ) : (
          groups.map((group, groupIndex) => {
            const open = isOpen(group);
            // Index-based, not key-based: the key is a (manufacturer, line)
            // pair whose free-text halves are not safe as a DOM id.
            const panelId = `refresh-line-${assetClassId}-${groupIndex}`;
            return (
              <Fragment key={group.key}>
                <h4 className="border-t border-zinc-100">
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={() => setToggled((prev) => ({ ...prev, [group.key]: !open }))}
                    className="flex w-full items-center gap-2 bg-zinc-50/60 px-3 py-2 text-left transition-colors hover:bg-zinc-100"
                  >
                    {open ? (
                      <ChevronDown className="size-3.5 shrink-0 text-zinc-400" aria-hidden />
                    ) : (
                      <ChevronRight className="size-3.5 shrink-0 text-zinc-400" aria-hidden />
                    )}
                    <span
                      title={group.heading}
                      className="min-w-0 truncate text-xs font-medium text-zinc-800"
                    >
                      {group.heading}
                    </span>
                    <span className="text-[13px] tabular-nums text-zinc-500">
                      {group.candidates.length}
                    </span>
                  </button>
                </h4>
                <div id={panelId} hidden={!open}>
                  {group.candidates.map((cand) => {
                    const selected = cand.skuId === selectedSkuId;
                    const name = candidateLabel(cand);
                    return (
                      <button
                        key={cand.skuId}
                        type="button"
                        title={name}
                        aria-pressed={selected}
                        onClick={() => onSelect(cand.skuId, cand.consolidationRatio)}
                        className={`grid w-full items-center border-t border-zinc-100 px-3 py-2 pl-8 text-left font-mono text-[13px] transition-colors ${
                          selected ? 'bg-brand-50' : 'hover:bg-zinc-50'
                        }`}
                        style={{ gridTemplateColumns: GRID_COLS }}
                      >
                        <span
                          className={`size-3 rounded-full border ${
                            selected ? 'border-brand-600 bg-brand-600' : 'border-zinc-300'
                          }`}
                        />
                        <span className="break-words pr-3 font-sans leading-snug text-zinc-800">
                          {name}
                        </span>
                        <span className="text-right text-zinc-700">
                          {cand.fp16Tflops != null ? cand.fp16Tflops.toLocaleString() : '—'}
                        </span>
                        <span className="text-right text-zinc-700">
                          {cand.tdpWatts != null ? cand.tdpWatts.toLocaleString() : '—'}
                        </span>
                        <span className="text-right text-zinc-700">
                          {cand.consolidationRatio != null
                            ? `${cand.consolidationRatio.toFixed(2)}×`
                            : '1.00× (default)'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}
